import { useState, FormEvent } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Clock, Car, CheckCircle2, Shield, Phone, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import heroImage from "@/assets/audi-city.png";
import LivePriceQuoteTool from "@/components/LivePriceQuoteTool";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

const Book = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    service: "",
    pickup: "",
    dropoff: "",
    date: "",
    time: "",
    passengers: "",
    requests: ""
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation
    if (!formData.service || !formData.pickup || !formData.dropoff || 
        !formData.date || !formData.time || !formData.passengers) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    // Build Square URL with parameters
    const squareBaseUrl = 'http://ricksblackcarservicesantonio.square.site';
    const params = new URLSearchParams({
      service_type: formData.service,
      pickup_location: formData.pickup,
      dropoff_location: formData.dropoff,
      booking_date: formData.date,
      pickup_time: formData.time,
      number_of_passengers: formData.passengers,
      special_requests: formData.requests || 'None',
      booking_source: 'ricks_website',
      notes: `Pickup: ${formData.pickup} | Drop-off: ${formData.dropoff} | Service: ${formData.service} | Passengers: ${formData.passengers}`
    });

    const squareUrl = `${squareBaseUrl}?${params.toString()}`;

    // Simulate brief loading then redirect
    setTimeout(() => {
      setIsLoading(false);
      toast({
        title: "Opening Secure Booking",
        description: "You'll be redirected to complete your reservation.",
      });
      
      setTimeout(() => {
        window.open(squareUrl, '_blank');
      }, 1000);
    }, 1500);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Set minimum date to today
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative h-[80vh] overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage}
              alt="Luxury black sedan - Rick's Car Service San Antonio" 
              className="w-full h-full object-cover brightness-50"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/90" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10 text-center space-y-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground drop-shadow-2xl">
              Book Your <span className="text-primary">Luxury Experience</span>
            </h1>
            <p className="text-xl md:text-2xl text-foreground/90 max-w-3xl mx-auto drop-shadow-lg font-light">
              Professional. Punctual. Elegant. Experience premier black car service for airport transfers, corporate travel, and special events.
            </p>
            <p className="text-lg text-primary/90 font-semibold">
              24/7 Availability • Licensed & Insured Chauffeurs
            </p>
            <Button 
              onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-luxury text-lg px-10 py-6 mt-4 shadow-2xl hover:shadow-primary/50"
            >
              Get Instant Quote
            </Button>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16 bg-card/50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="card-luxury text-center hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6 space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Punctuality Guaranteed</h3>
                  <p className="text-sm text-muted-foreground">
                    Chauffeurs arrive 15 minutes early for every booking
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury text-center hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6 space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                    <Car className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Luxury Fleet</h3>
                  <p className="text-sm text-muted-foreground">
                    Mercedes, Cadillac, Lincoln - Premium leather interiors
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury text-center hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6 space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">24/7 Availability</h3>
                  <p className="text-sm text-muted-foreground">
                    Early morning flights or late-night events - always ready
                  </p>
                </CardContent>
              </Card>

              <Card className="card-luxury text-center hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6 space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Professional Service</h3>
                  <p className="text-sm text-muted-foreground">
                    Licensed, insured, uniformed chauffeurs
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Booking Form Section */}
        <section id="booking-form" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12 space-y-4">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                  Book Your Ride
                </h2>
                <div className="w-24 h-1 bg-primary mx-auto" />
                <p className="text-lg text-muted-foreground">
                  Fill out the form below and we'll redirect you to our secure Square booking system
                </p>
              </div>

              <Card className="card-luxury border-primary/20 bg-card/90 backdrop-blur-xl">
                <CardContent className="p-8 md:p-12">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-primary font-semibold uppercase text-xs tracking-wider">
                          Service Type *
                        </Label>
                        <Select 
                          value={formData.service} 
                          onValueChange={(value) => handleInputChange('service', value)}
                          required
                        >
                          <SelectTrigger className="bg-background/50 border-border/50 focus:border-primary h-12">
                            <SelectValue placeholder="Select Service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="airport">Airport Transfer</SelectItem>
                            <SelectItem value="hourly">Hourly Chauffeur</SelectItem>
                            <SelectItem value="corporate">Corporate Travel</SelectItem>
                            <SelectItem value="wedding">Weddings & Events</SelectItem>
                            <SelectItem value="tour">City Tours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="passengers" className="text-primary font-semibold uppercase text-xs tracking-wider">
                          Passengers *
                        </Label>
                        <Select 
                          value={formData.passengers} 
                          onValueChange={(value) => handleInputChange('passengers', value)}
                          required
                        >
                          <SelectTrigger className="bg-background/50 border-border/50 focus:border-primary h-12">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-6">Luxury SUV (up to 6 passengers)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="pickup" className="text-primary font-semibold uppercase text-xs tracking-wider">
                          Pickup Location *
                        </Label>
                        <Input
                          id="pickup"
                          type="text"
                          placeholder="e.g., 123 Main St, City, State"
                          value={formData.pickup}
                          onChange={(e) => handleInputChange('pickup', e.target.value)}
                          className="bg-background/50 border-border/50 focus:border-primary h-12"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="dropoff" className="text-primary font-semibold uppercase text-xs tracking-wider">
                          Drop-off Location *
                        </Label>
                        <Input
                          id="dropoff"
                          type="text"
                          placeholder="e.g., Airport Terminal"
                          value={formData.dropoff}
                          onChange={(e) => handleInputChange('dropoff', e.target.value)}
                          className="bg-background/50 border-border/50 focus:border-primary h-12"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="date" className="text-primary font-semibold uppercase text-xs tracking-wider">
                          Date *
                        </Label>
                        <Input
                          id="date"
                          type="date"
                          min={today}
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          className="bg-background/50 border-border/50 focus:border-primary h-12"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="time" className="text-primary font-semibold uppercase text-xs tracking-wider">
                          Pickup Time *
                        </Label>
                        <Input
                          id="time"
                          type="time"
                          value={formData.time}
                          onChange={(e) => handleInputChange('time', e.target.value)}
                          className="bg-background/50 border-border/50 focus:border-primary h-12"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="requests" className="text-primary font-semibold uppercase text-xs tracking-wider">
                        Special Requests
                      </Label>
                      <Textarea
                        id="requests"
                        placeholder="Flight details, special accommodations, etc. (optional)"
                        value={formData.requests}
                        onChange={(e) => handleInputChange('requests', e.target.value)}
                        maxLength={200}
                        rows={3}
                        className="bg-background/50 border-border/50 focus:border-primary resize-none"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.requests.length}/200 characters
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full btn-luxury text-lg py-6 font-bold uppercase tracking-wider"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        'Book Now with Square'
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground pt-4">
                      <Shield className="w-4 h-4 text-primary" />
                      <span>Secure booking powered by Square • Instant confirmation • Licensed & insured</span>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Live Price Quote Tool */}
        <LivePriceQuoteTool />

        {/* How It Works */}
        <section className="py-20 bg-card/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                How It Works
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto" />
            </div>

            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary text-background flex items-center justify-center text-3xl font-bold shadow-lg shadow-primary/30">
                  1
                </div>
                <h3 className="text-2xl font-bold text-primary">Fill Out Your Details</h3>
                <p className="text-muted-foreground">
                  Enter your pickup/drop-off locations, preferred date and time, service type, and number of passengers
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary text-background flex items-center justify-center text-3xl font-bold shadow-lg shadow-primary/30">
                  2
                </div>
                <h3 className="text-2xl font-bold text-primary">Get Instant Quote</h3>
                <p className="text-muted-foreground">
                  Our system instantly calculates your fare and shows availability with complete transparency
                </p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary text-background flex items-center justify-center text-3xl font-bold shadow-lg shadow-primary/30">
                  3
                </div>
                <h3 className="text-2xl font-bold text-primary">Complete Booking</h3>
                <p className="text-muted-foreground">
                  Redirect to our secure Square payment system for instant confirmation via email
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                What Our Clients Say
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Card className="card-luxury hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-8 space-y-4">
                  <div className="text-primary text-6xl font-serif leading-none">"</div>
                  <p className="text-foreground/90 italic">
                    Rick's Black Car Service transformed our corporate event. The chauffeur was impeccably professional, the vehicle spotless, and they arrived 15 minutes early as promised. Outstanding service!
                  </p>
                  <div className="pt-4 border-t border-border/50">
                    <p className="font-bold text-primary">Sarah M.</p>
                    <p className="text-sm text-muted-foreground">Event Coordinator</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-8 space-y-4">
                  <div className="text-primary text-6xl font-serif leading-none">"</div>
                  <p className="text-foreground/90 italic">
                    Best airport transfer experience ever! After a long flight, having a luxury car waiting with a courteous driver made all the difference. Will definitely use them for all future trips.
                  </p>
                  <div className="pt-4 border-t border-border/50">
                    <p className="font-bold text-primary">Michael R.</p>
                    <p className="text-sm text-muted-foreground">Business Traveler</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-8 space-y-4">
                  <div className="text-primary text-6xl font-serif leading-none">"</div>
                  <p className="text-foreground/90 italic">
                    Our wedding day was perfect, thanks to Rick's service. The stretch SUV was beautiful, the driver incredibly accommodating, and everything ran on time. Highly recommend for special occasions!
                  </p>
                  <div className="pt-4 border-t border-border/50">
                    <p className="font-bold text-primary">Emily & James</p>
                    <p className="text-sm text-muted-foreground">Newlyweds</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-card/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="card-luxury hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-8 space-y-4">
                  <Phone className="w-12 h-12 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground">Need Help?</h3>
                  <p className="text-muted-foreground">
                    Our team is available 24/7 to assist with your booking or answer any questions.
                  </p>
                  <Button 
                    className="btn-luxury w-full" 
                    asChild
                  >
                    <a href="tel:+12102967409">
                      Call: 210-296-7409
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="card-luxury hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-8 space-y-4">
                  <MapPin className="w-12 h-12 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground">Service Area</h3>
                  <p className="text-muted-foreground">
                    Serving San Antonio and surrounding areas with professional black car service.
                  </p>
                  <p className="text-primary font-semibold">
                    16034 Rough Oak Street<br />
                    San Antonio, TX 78232
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Book;
